import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useApiClient } from '@/api/client'
import type { Dashboard } from './types'
import type { ReportChartInstance } from '@/features/reports/types'

export const dashboardKeys = {
  all: (slug: string) => ['dashboards', slug] as const,
  list: (slug: string) => ['dashboards', slug, 'list'] as const,
  detail: (slug: string, id: string) => ['dashboards', slug, id] as const,
  project: (slug: string, projectId: string) => ['dashboards', slug, 'project', projectId] as const,
}

export function useDashboards(workspaceSlug: string) {
  const { fetchWithAuth } = useApiClient()
  return useQuery({
    queryKey: dashboardKeys.list(workspaceSlug),
    queryFn: () =>
      fetchWithAuth<{ items: Dashboard[] }>(
        `/api/v1/workspaces/${workspaceSlug}/dashboards`,
      ).then((r) => r.items),
    staleTime: 60_000,
  })
}

export function useDashboard(workspaceSlug: string, id: string | undefined) {
  const { fetchWithAuth } = useApiClient()
  return useQuery({
    queryKey: dashboardKeys.detail(workspaceSlug, id ?? ""),
    queryFn: () =>
      fetchWithAuth<Dashboard>(`/api/v1/workspaces/${workspaceSlug}/dashboards/${id}`),
    enabled: !!id,
    staleTime: 60_000,
  })
}

export function useProjectDashboard(workspaceSlug: string, projectId: string) {
  const { fetchWithAuth } = useApiClient()
  return useQuery({
    queryKey: dashboardKeys.project(workspaceSlug, projectId),
    queryFn: () =>
      fetchWithAuth<Dashboard>(
        `/api/v1/workspaces/${workspaceSlug}/dashboards/project/${projectId}`,
      ),
    enabled: !!projectId,
    staleTime: 60_000,
  })
}

export function useDashboardMutations(workspaceSlug: string) {
  const { fetchWithAuth } = useApiClient()
  const qc = useQueryClient()

  const invalidateList = () => qc.invalidateQueries({ queryKey: dashboardKeys.list(workspaceSlug) })

  const create = useMutation({
    mutationFn: (name: string) =>
      fetchWithAuth<Dashboard>(`/api/v1/workspaces/${workspaceSlug}/dashboards`, {
        method: 'POST',
        body: JSON.stringify({ name }),
      }),
    onSuccess: invalidateList,
  })

  const updateCharts = useMutation({
    mutationFn: ({ id, charts }: { id: string; charts: ReportChartInstance[] }) =>
      fetchWithAuth<Dashboard>(`/api/v1/workspaces/${workspaceSlug}/dashboards/${id}`, {
        method: 'PUT',
        body: JSON.stringify({ charts }),
      }),
    onSuccess: (data) => {
      qc.setQueryData(dashboardKeys.detail(workspaceSlug, data.id), data)
      if (data.project_scope) {
        qc.setQueryData(dashboardKeys.project(workspaceSlug, data.project_scope), data)
      }
    },
  })

  const rename = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      fetchWithAuth<Dashboard>(`/api/v1/workspaces/${workspaceSlug}/dashboards/${id}`, {
        method: 'PUT',
        body: JSON.stringify({ name }),
      }),
    onSuccess: invalidateList,
  })

  const remove = useMutation({
    mutationFn: (id: string) =>
      fetchWithAuth<void>(`/api/v1/workspaces/${workspaceSlug}/dashboards/${id}`, {
        method: 'DELETE',
      }),
    onSuccess: invalidateList,
  })

  const toggleFavorite = useMutation({
    mutationFn: (id: string) =>
      fetchWithAuth<{ favorited: boolean }>(
        `/api/v1/workspaces/${workspaceSlug}/dashboards/${id}/favorite`,
        { method: 'POST' },
      ),
    onSuccess: invalidateList,
  })

  return { create, updateCharts, rename, remove, toggleFavorite }
}
